import { Group, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getRuntimeConfig } from '../shared/config/runtime';
import { formatAppVersion } from '../shared/version';
import { checkForUpdate } from '../shared/versionCheck';

/**
 * Footer bar, modelled on the GOS footer: copyright on the left, "Version : x"
 * on the right, pinned to the bottom of the window.
 *
 * Two variants, matching GOS's two usages of the same component:
 *   - "solid"       inside the app shell, navy with white text like the header
 *                   (GOS: class "footer fixed-bottom text-white")
 *   - "transparent" on the public pages, dimmed text over the page background
 *                   (GOS: adds "background-transparent" on the login screen)
 *
 * The version replaces the old corner VersionBadge — it says the same thing in
 * a place that doesn't float over page content, and outside production it
 * carries the environment suffix ("0.3.1020 - stage").
 *
 * TAPPING THE VERSION CHECKS FOR AN UPDATE.
 * It was only reachable from the profile menu, which meant signing in first — so somebody stuck on
 * an old build at the Sign In screen had no way to get off it. This footer is already on the login
 * and registration pages as well as inside the shell, and the version is exactly what you would
 * poke at to ask "am I current?", so the affordance goes where the question already is.
 */

interface Props {
  variant?: 'solid' | 'transparent';
}

const HEIGHT = 28;

export function AppFooter({ variant = 'solid' }: Props) {
  const { t } = useTranslation();

  let version: string | null = null;
  try {
    version = formatAppVersion(getRuntimeConfig().environment);
  } catch {
    // Runtime config not loaded (shouldn't happen post-bootstrap) — the
    // copyright still renders; only the version is omitted.
  }

  const solid = variant === 'solid';
  const color = solid ? 'rgba(255,255,255,0.85)' : 'var(--mantine-color-dimmed)';

  return (
    <Group
      h={HEIGHT}
      px="md"
      justify="space-between"
      wrap="nowrap"
      gap="sm"
      style={
        solid
          ? undefined
          : {
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
              // The public pages pin this themselves, so nothing else reserves the iOS insets for
              // them: without these the version reads underneath the home indicator, and in
              // landscape underneath the Dynamic Island. In the shell AppShell.Footer owns this.
              paddingBottom: 'calc(env(safe-area-inset-bottom))',
              paddingLeft: 'calc(var(--mantine-spacing-md) + env(safe-area-inset-left))',
              paddingRight: 'calc(var(--mantine-spacing-md) + env(safe-area-inset-right))',
              height: 'auto',
              minHeight: HEIGHT,
            }
      }
    >
      {/* The copyright truncates and the version never does: on a 320px-wide
          handheld the two don't both fit, and the build/environment is the part
          someone actually needs to read. */}
      <Text size="xs" c={color} truncate style={{ flex: 1, minWidth: 0 }}>
        {t('footer.copyright', { year: new Date().getFullYear() })}
      </Text>
      {version && (
        <UnstyledButton
          onClick={() => void checkForUpdate(true)}
          aria-label={t('shell.checkForUpdates')}
          title={t('shell.checkForUpdates')}
          style={{ flexShrink: 0 }}
        >
          <Text
            size="xs"
            c={color}
            td="underline"
            style={{
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {t('footer.version')} : {version}
          </Text>
        </UnstyledButton>
      )}
    </Group>
  );
}

/** Height the AppShell must reserve for the footer. */
export const APP_FOOTER_HEIGHT = HEIGHT;
